import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { assetUrl } from '../../lib/api';

export const HeroSlider = ({ slides = [] }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => setCurrent((c) => (c + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    timerRef.current = setInterval(next, 6500);
    return () => clearInterval(timerRef.current);
  }, [next, paused, slides.length]);

  if (!slides.length) return null;

  return (
    <section
      data-testid="home-hero-carousel"
      className="relative w-full min-h-[520px] sm:min-h-[640px] overflow-hidden bg-[#1a1a3a]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 fade-slide ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
          style={{
            backgroundImage: `url(${assetUrl(slide.image)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden={i !== current}
        >
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center min-h-[520px] sm:min-h-[640px]">
            <div className="max-w-3xl py-20">
              <h2 className="text-white font-extrabold text-3xl sm:text-5xl lg:text-[3.4rem] leading-tight tracking-tight">
                {slide.title}
              </h2>
              <p className="mt-5 text-white/95 text-base sm:text-lg font-medium leading-7 max-w-2xl">{slide.subtitle}</p>
              {slide.cta_text && (
                <div className="mt-8">
                  <Link
                    to={slide.cta_link || '/'}
                    data-testid="home-hero-slide-cta"
                    className="inline-flex border-2 border-white text-white font-bold rounded-md px-7 py-3.5 transition-colors duration-200 hover:bg-white hover:text-[#26247B]"
                  >
                    {slide.cta_text}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Arrows */}
      <button
        data-testid="home-hero-prev-button"
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/90 hover:bg-white text-[#26247B] flex items-center justify-center transition-colors duration-200 shadow-lg"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        data-testid="home-hero-next-button"
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/90 hover:bg-white text-[#26247B] flex items-center justify-center transition-colors duration-200 shadow-lg"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots */}
      <div data-testid="home-hero-pagination" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-3 w-3 rounded-full transition-colors duration-200 ${i === current ? 'bg-[#F4A300]' : 'bg-white/60 hover:bg-white'}`}
          />
        ))}
      </div>
    </section>
  );
};
